import axios from "axios";
import { BASE_URL } from "../constants/APIConstants";
import {
  apiRequest,
  authConfig,
  deleteResource,
  getResource,
} from "../api/http";

// "General content": movies and other catalogue items (ads, broadcasts...).

export function getMovieList() {
  return getResource("/api/admin/get-movie-list");
}

export function getMovie(generalContent_id) {
  return getResource(
    `/api/general-content/get-general-content/${generalContent_id}`,
    {
      select: (data) =>
        data.generalContentObj !== null ? data.generalContentObj : [],
      fallback: [],
    }
  );
}

export function updateMovie(obj, movie_id) {
  return apiRequest(
    "put",
    `/api/general-content/update-general-content/${movie_id}`,
    { data: obj }
  );
}

export function deleteGeneralContent(generalContent_id) {
  return deleteResource(
    `/api/general-content/delete-general-content/${generalContent_id}`
  );
}

/** `contentURL` is the media type segment of the endpoint (e.g. movie, tv-shows). */
export function addCrewMembers(contentURL, generalContent_id, data) {
  return apiRequest(
    "put",
    `/api/${contentURL}/add-crew-members/${generalContent_id}`,
    { data, fallback: [] }
  );
}

// Comments
export function getComments(generalContent_id, type) {
  return getResource(
    `/api/comments/get-general-content-comments/${generalContent_id}/${type}`,
    { select: (data) => data.comments || [], fallback: [] }
  );
}

export function getEpisodeComments(generalContent_id, type) {
  return getResource(
    `/api/comments/get-comment-episode/${generalContent_id}/${type}`,
    { select: (data) => data?.comments || [], fallback: [] }
  );
}

// Media upload bookkeeping. These resolve with the raw axios response, which
// the redux media actions consume as is.

/** Stores the JW media id / duration on a media document. */
export function updateMediaInfo(mediaObjId, body) {
  return axios.put(
    `${BASE_URL}/api/media/upload-media-id/${mediaObjId}`,
    body,
    authConfig()
  );
}

/** Links an uploaded JW trailer to a general content. */
export function setTrailerMediaId(generalContent_id, body) {
  return axios.post(
    `${BASE_URL}/api/trailer/upload-trailer/${generalContent_id}`,
    body,
    authConfig()
  );
}

/**
 * Push notification announcing a published video to every device. Goes through
 * the notifications API so that it shows in the Notifications history.
 */
export function sendTopicNotification(notification) {
  return apiRequest("post", "/api/admin/notifications", {
    data: {
      kind: "video",
      videoId: notification.video_id,
      title: notification.title,
      body: notification.description,
      title_en: notification.title_translated,
      body_en: notification.description_translated,
      audience: { type: "all" },
      // Envoi étalé en vagues (forte affluence attendue)
      spread: notification.spread === true,
      source: "publish",
    },
    rethrow: true,
  });
}

// Creation endpoints reject on failure; callers check the `saved*` field.

export function createGeneralContent(generalInfo) {
  return apiRequest("post", "/api/media/create-general-content", {
    data: generalInfo,
    rethrow: true,
  });
}

/**
 * Puts a video in the trending selection or takes it out. Several videos can
 * be trending: the website shows them all, the app the last one put. Resolves
 * with { isTrending, trendingAt, trendingCount }; rejects with the API message.
 */
export const setVideoTrending = (generalContent_id, trending) =>
  apiRequest("post", `/api/v2/videos/admin/trending/${generalContent_id}`, {
    data: { trending },
    rethrow: true,
  });

/** Scheduled release of a video: { scheduledReleaseAt, notification } (null values when none). */
export const getVideoSchedule = (generalContent_id) =>
  apiRequest("get", `/api/admin/videos/${generalContent_id}/schedule`, { rethrow: true });

/** Schedules the release: { at, notify: { title, body, title_en, body_en, spread } | null }. Rejects with the API message. */
export const scheduleVideoRelease = (generalContent_id, body) =>
  apiRequest("post", `/api/admin/videos/${generalContent_id}/schedule`, { data: body, rethrow: true });

/** Cancels the scheduled release (the video stays upcoming). */
export const cancelVideoRelease = (generalContent_id) =>
  apiRequest("delete", `/api/admin/videos/${generalContent_id}/schedule`, { rethrow: true });
